<template>
  <iButton
    v-if="shouldShowAction"
    :size="getOptionValue(ButtonSize)"
    :variant="getOptionValue(ButtonType)"
    :width="getOptionValue(LayoutWidth)"
    :height="getOptionValue(LayoutHeight)"
    :icon-color="myIconColor"
    :style-overrides="actionButtonStyleOverrides"
    :border-top="myBorderSides.top"
    :border-bottom="myBorderSides.bottom"
    :border-left="myBorderSides.left"
    :border-right="myBorderSides.right"
    :data-action-icon="myIcon || null"
    :aria-label="actionAriaLabel"
    @click="() => !editMode && handleClick()"
  >
    <PrefixSuffixWrapper
      :prefix="myPrefix"
      :suffix="mySuffix"
      :style-bindings="textOnlyAttributes"
      :icon-color="myIconColor"
      :wrapper-style-overrides="actionInnerButtonStyleOverrides"
    >
      <iRow vertical-align="middle" gap="none" wrap="nowrap">
        <iIcon
          v-if="myIcon && myIconPosition === 'beforeText'"
          :is-loading="isLoading"
          :icon="myIcon"
          :name="myIcon"
          :size="myIconSize"
          :style-overrides="{
            primaryVariantColor: myIconColor,
            marginRight: 'extraSmall',
          }"
        />
        <iRichText
          v-if="!textIsEmpty && myIconPosition === 'beforeText'"
          ref="richTextElement"
          :model-value="initialValue"
          prevent-new-line
          :preview="previewMode"
          :show-toolbar="false"
          :show-bubble-menu="true"
          :tools-to-disable="['text-align', 'link', 'bullet-list']"
          :style-overrides="{
            borderWidth: 'none',
            borderRadius: 'none',
            paddingLeft: 'none',
            paddingRight: 'none',
            paddingTop: 'none',
            paddingBottom: 'none',
          }"
          v-bind="textOnlyAttributes"
          @blur="handleBlur"
          @keydown.enter="handleBlur"
          @update:model-value="handleTextOptionUpdate($event)"
        />
        <iColumn
          v-if="!countPending && myValue !== undefined && myValue !== '' && myValue !== null"
          width="hug"
          :style-overrides="{
            paddingLeft: myIconPosition === 'afterText' ? 'none' : 'extraSmall',
            paddingRight: myIconPosition === 'afterText' ? 'extraSmall' : 'none',
          }"
        >
          <iText
            :style-overrides="{
              standardFontColor: myCountColor,
              standardFontSize: myCountSize
            }"
          >
            {{ myValue }}
          </iText>
        </iColumn>
        <iRichText
          v-if="!textIsEmpty && myIconPosition === 'afterText'"
          ref="richTextElement"
          :model-value="initialValue"
          prevent-new-line
          :preview="previewMode"
          :show-toolbar="false"
          :show-bubble-menu="true"
          :tools-to-disable="['text-align', 'link', 'bullet-list']"
          :style-overrides="{
            borderWidth: 'none',
            borderRadius: 'none',
            paddingLeft: 'none',
            paddingRight: 'none',
            paddingTop: 'none',
            paddingBottom: 'none',
          }"
          v-bind="textOnlyAttributes"
          @blur="handleBlur"
          @keydown.enter="handleBlur"
          @update:model-value="handleTextOptionUpdate($event)"
        />
        <iIcon
          v-if="myIcon && myIconPosition === 'afterText'"
          :is-loading="isLoading"
          :icon="myIcon"
          :name="myIcon"
          :size="myIconSize"
          :style-overrides="{
            primaryVariantColor: myIconColor,
            marginLeft: 'extraSmall',
          }"
        />
      </iRow>
    </PrefixSuffixWrapper>
  </iButton>
</template>

<script>
import PrefixSuffixWrapper from "%/dynamic-component-editor/components/PrefixSuffixWrapper.vue";
import {
  ActionElementMixin,
} from "%/dynamic-component-editor/standard-elements/action-element/action-element-mixin";
import BaseButtonElement
  from "%/dynamic-component-editor/standard-elements/button-element/BaseButtonElement.vue";

export default {
  name: "DynamicComponentActionElement",
  components: { PrefixSuffixWrapper },
  extends: BaseButtonElement,
  mixins: [ActionElementMixin],
  computed: {
    actionInnerButtonStyleOverrides() {
      return {
        standardPaddingLeft: "none",
        standardPaddingRight: "none",
        standardPaddingTop: "none",
        standardPaddingBottom: "none",
        smallPaddingLeft: "none",
        smallPaddingRight: "none",
        smallPaddingTop: "none",
        smallPaddingBottom: "none",
        largePaddingLeft: "none",
        largePaddingRight: "none",
        largePaddingTop: "none",
        largePaddingBottom: "none",
        extraSmallPaddingLeft: "none",
        extraSmallPaddingRight: "none",
        extraSmallPaddingTop: "none",
        extraSmallPaddingBottom: "none",
      };
    },
    suppressPaddingAdjustment() {
      return false;
    },
    actionButtonStyleOverrides() {
      const overrides = { ...this.buttonStyleOverrides };
      if (!this.suppressPaddingAdjustment) {
        return overrides;
      }

      const borderWidthKey = `${this.buttonVariant}VariantBorderWidth`;
      const borderPx = this.resolveSpacingOrBorderTokenToPx(
        overrides[borderWidthKey],
        "size.border",
      );
      const adjustment = borderPx < 2 ? 2 - borderPx : 0;
      if (adjustment === 0) {
        return overrides;
      }

      const size = this.myButtonSize;
      for (const side of ["Top", "Right", "Bottom", "Left"]) {
        const key = `${size}Padding${side}`;
        if (overrides[key] === undefined) {
          continue;
        }
        const px = this.resolveSpacingOrBorderTokenToPx(overrides[key], "size.spacing");
        overrides[key] = `${px - adjustment}px`;
      }
      return overrides;
    },
  },
  methods: {
    resolveSpacingOrBorderTokenToPx(value, themePath) {
      if (!value || value === "none") {
        return 0;
      }
      if (typeof value === "string" && value.endsWith("px")) {
        return parseInt(value, 10) || 0;
      }
      const properties = this.$getStyleValuePropertiesFromTheme(themePath);
      const resolved = properties?.[value];
      if (!resolved) {
        return 0;
      }
      return parseInt(String(resolved).replace("px", ""), 10) || 0;
    },
  },
};
</script>
